import type { PropsWithChildren } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { StepProgress } from "../progress/StepProgress";
import { LanguageSwitcher } from "../lang/LanguageSwitcher";

export interface OnboardingStepLayoutProps extends PropsWithChildren {
  title: string;
  subtitle?: string;
  step: number;
  totalSteps: number;

  onNext?: () => void;
  nextLabel?: string;
  loadingNext?: boolean;

  onBack?: () => void;
  backLabel?: string;

  showProgress?: boolean;
  showSideImage?: boolean;
  showActions?: boolean;
}

const asset = (p: string) =>
  `${import.meta.env.BASE_URL}${encodeURI(p.replace(/^\/+/, ""))}`;

export function OnboardingStepLayout({
  title,
  subtitle,
  step,
  totalSteps,
  children,
  onNext,
  nextLabel = "Next",
  loadingNext,
  onBack,
  backLabel = "Back",
  showProgress = true,
  showSideImage = true,
  showActions = true,
}: OnboardingStepLayoutProps) {
  const hasActions = showActions && (!!onBack || !!onNext);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: showSideImage ? 1320 : 920 },
        mx: { xs: 0, md: "auto" },
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: showSideImage ? "1fr 1.05fr" : "1fr",
        },
        borderRadius: { xs: 0, md: "32px" },
        overflow: "hidden",
        bgcolor: "#fff",
        minHeight: { xs: "100dvh", md: "auto" },
      }}
    >
      {/* COLONNA IMMAGINE (solo desktop) */}
      {showSideImage && (
        <Box sx={{ display: { xs: "none", md: "block" }, height: 1000 }}>
          <Box
            component="img"
            src={asset("background/img-bg-left-2.jpg")}
            alt="Atlante background"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      )}

      {/* COLONNA CONTENUTO */}
      <Box
        component={onNext ? "form" : "div"}
        onSubmit={
          onNext
            ? (e: React.FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                onNext?.();
              }
            : undefined
        }
        sx={{
          px: { xs: 2, md: 7 },
          py: { xs: 2, md: 6 },
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          height: { xs: "auto", md: 1000 },
          minHeight: { xs: "100dvh", md: 1000 },
        }}
      >
        <Stack
          spacing={{ xs: 2.5, md: 4 }}
          sx={{
            flex: { xs: "unset", md: 1 },
            minHeight: { xs: "auto", md: 0 },
          }}
        >
          {/* HEADER: LOGO + LANGUAGE SWITCHER */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: { xs: 44, md: 72 },
            }}
          >
            <Box
              component="img"
              src={asset("logos/png/ATLANTE LOGO v3_inline RGB.png")}
              alt="Atlante"
              sx={{
                height: { xs: 120, sm: 52, md: 170 },
                width: "auto",
                maxWidth: { xs: 180, sm: 220, md: 250 },
                marginTop: { xs: 0, md: -12 },
                marginBottom: { xs: 0, md: -8 },
              }}
            />

            <Box sx={{ position: "absolute", right: 0, top: 0 }}>
              <LanguageSwitcher />
            </Box>
          </Box>

          {/* PROGRESS + TITOLI */}
          <Stack spacing={{ xs: 1.5, md: 2 }}>
            {showProgress && (
              <StepProgress step={step} totalSteps={totalSteps} />
            )}

            <Typography
              variant="h4"
              sx={{
                color: "#0A0A0A",
                fontSize: { xs: "1.55rem", md: "2.1rem" },
                lineHeight: { xs: 1.15, md: 1.2 },
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  color: "#6A7A89",
                  maxWidth: 520,
                  fontSize: "0.95rem",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>

          {/* CONTENUTO */}
          <Box
            sx={{
              flex: { xs: "unset", md: 1 },
              minHeight: { xs: "auto", md: 0 },
              overflowY: { xs: "visible", md: "auto" },
              WebkitOverflowScrolling: "touch",
              pr: { xs: 0, md: 1 },
              "&::-webkit-scrollbar": { width: { xs: 0, md: 8 } },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#ECEFF2",
                borderRadius: 999,
              },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                background: "linear-gradient(180deg, #6CCB8B 0%, #3975D0 100%)",
              },
              scrollbarWidth: { xs: "auto", md: "thin" },
              scrollbarColor: { xs: "auto", md: "#39B2AC #ECEFF2" },
            }}
          >
            {children}
          </Box>

          {/* AZIONI (opzionali) */}
          {hasActions && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 2,
                justifyContent: "space-between",
                alignItems: "center",
                pt: { xs: 1, md: 0 },
              }}
            >
              {onBack ? (
                <Button
                  type="button"
                  variant="text"
                  onClick={onBack}
                  sx={{
                    textTransform: "none",
                    color: "#4A5568",
                    px: 0,
                    minWidth: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {backLabel}
                </Button>
              ) : (
                <Box />
              )}

              {onNext ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loadingNext}
                  sx={{
                    px: { xs: 4, sm: 10 },
                    py: { xs: 1.2, sm: 1.8 },
                    borderRadius: 999,
                    textTransform: "none",
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    background:
                      "linear-gradient(90deg, #6CCB8B 0%, #3975D0 100%)",
                    whiteSpace: "nowrap",
                    width: "auto",
                  }}
                >
                  {nextLabel}
                </Button>
              ) : (
                <Box />
              )}
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
